import React, { useMemo } from 'react';
import { CardDefault, DataTable, Text } from '@spark';
import ImprovementPill, {
  computeImprovement,
  computeSignedPercentChange,
} from './ImprovementPill';

/**
 * Render a StatRow numeric value with its unit, or "-" when it's null/undefined.
 * Avoids the previous "nullms" rendering when the comparison column had no data.
 */
function renderStatValue(value, row) {
  if (value == null) {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }
  return <Text variant="base-regular">{`${value}${row?.unit || ''}`}</Text>;
}

/**
 * Generic stats comparison table used for Inference Latency, Throughput, and
 * Response Length tables.
 *
 * When `showComparison` is false, the comparison and improvement columns are
 * omitted entirely so the remaining two columns (metric + current) take up
 * the full width. This matches the rule:
 *   - No Compare: selection              -> hide.
 *   - Compare: is teacher / results file -> hide (not in performance_metrics).
 *   - Compare: is an unseeded base model -> hide.
 *   - Compare: is a seeded SpecificAI
 *     version with a resolvable base     -> show.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {import('./data/performanceMetricsApi').StatRow[]} props.rows
 * @param {string} [props.comparisonModelLabel]
 * @param {string} props.currentModelLabel
 * @param {boolean} [props.showComparison]      Default false.
 * @param {boolean} [props.showMeasured]        Add a measured-run column (`row.measured`).
 * @param {string} [props.measuredLabel]
 * @param {('lower-is-better'|'higher-is-better')} [props.direction]
 * @param {string} [props.improvementLabel]     Header label for the improvement column.
 * @param {'performance'|'signed-percent'} [props.improvementMode]  `signed-percent` = plain %, default Tag unless |Δ%|≥10 (red).
 * @param {boolean} [props.loading]
 * @param {import('react').ReactNode} [props.emptyText]
 */
export default function PerformanceStatsTable({
  title,
  rows,
  comparisonModelLabel,
  currentModelLabel,
  showComparison = false,
  showMeasured = false,
  measuredLabel = 'Measured serving-perf',
  direction = 'lower-is-better',
  improvementLabel = 'improvement',
  improvementMode = 'performance',
  loading = false,
  emptyText = 'This base model has no catalog baseline. That is not a failed serving-perf run.',
  style = {},
}) {
  const columns = useMemo(() => {
    const extraCount = Number(showComparison) + Number(showMeasured);
    const metricWidth = extraCount === 0 ? '50%' : extraCount === 1 ? '34%' : '20%';
    const valueWidth = extraCount === 0 ? '50%' : extraCount === 1 ? '33%' : '20%';

    const metricColumn = {
      title,
      dataIndex: 'metric',
      key: 'metric',
      width: metricWidth,
      render: (value) => (
        <Text variant="base-regular" style={{ color: '#111827' }}>{value}</Text>
      ),
    };

    const currentColumn = {
      title: currentModelLabel,
      dataIndex: 'current',
      key: 'current',
      width: valueWidth,
      align: 'left',
      render: (value, row) => renderStatValue(value, row),
    };

    const measuredColumn = {
      title: measuredLabel,
      dataIndex: 'measured',
      key: 'measured',
      width: valueWidth,
      align: 'left',
      render: (value, row) => renderStatValue(value, row),
    };

    const built = [metricColumn];
    if (showComparison) {
      built.push({
        title: comparisonModelLabel,
        dataIndex: 'comparison',
        key: 'comparison',
        width: valueWidth,
        align: 'left',
        render: (value, row) => renderStatValue(value, row),
      });
    }
    built.push(currentColumn);
    if (showMeasured) {
      built.push(measuredColumn);
    }
    if (showComparison) {
      built.push({
        title: improvementLabel,
        dataIndex: 'improvement',
        key: 'improvement',
        width: valueWidth,
        align: 'left',
        render: (_value, row) => {
          if (improvementMode === 'signed-percent') {
            const meta = computeSignedPercentChange({
              comparison: row.comparison,
              current: row.current,
            });
            return (
              <ImprovementPill
                variant="signed-percent"
                kind={meta.kind}
                text={meta.text}
                highlight={meta.highlight}
              />
            );
          }
          const meta = computeImprovement({
            comparison: row.comparison,
            current: row.current,
            direction,
          });
          return <ImprovementPill kind={meta.kind} text={meta.text} />;
        },
      });
    }
    return built;
  }, [
    title,
    comparisonModelLabel,
    currentModelLabel,
    measuredLabel,
    direction,
    improvementLabel,
    improvementMode,
    showComparison,
    showMeasured,
  ]);

  return (
    <div className="fs-unmask" style={{ marginBottom: 24, ...style }}>
      <DataTable
        rowKey="key"
        dataSource={rows}
        columns={columns}
        pagination={null}
        showHeader
        loading={loading}
        emptyText={emptyText}
      />
    </div>
  );
}
